import { useEffect, useRef } from 'react';

// GTM fires the GA4 page_view tag on this event instead of History Change, as History Change
// fires before the new page data and title have loaded.
export const VIRTUAL_PAGE_VIEW_EVENT = 'virtual_page_view';

/**
 * Pushes a `virtual_page_view` event with the page's data layer values to `window.dataLayer`
 * after each client-side navigation.
 *
 * The initial page's values are pushed once by the server-rendered data layer script, so the
 * value present on mount is skipped. Updates are only pushed once new page data has finished
 * loading, and never replace `window.dataLayer`, as GTM is attached to the original array.
 *
 * `pageTitle` is passed in from the page data rather than read from `document.title`, as Helmet
 * may not have updated the document title yet when this runs.
 */
export function useDataLayerSync(
  dataLayer: Record<string, unknown> | undefined,
  pageTitle: string | undefined,
  isLoading: boolean,
): void {
  const lastDataLayer = useRef(dataLayer);
  const lastPageLocation = useRef(typeof window !== 'undefined' ? window.location.href : undefined);

  useEffect(() => {
    // A failed fetch keeps the previous page data, so the reference check prevents duplicate pushes.
    if (isLoading || !dataLayer || dataLayer === lastDataLayer.current) {
      return;
    }

    lastDataLayer.current = dataLayer;

    // A hydration mismatch makes React render from scratch on the client, which refetches the
    // page already rendered by the server. Only a new location counts as a page view.
    const pageLocation = window.location.href;
    if (pageLocation === lastPageLocation.current) {
      return;
    }

    lastPageLocation.current = pageLocation;

    window.dataLayer?.push({
      ...dataLayer,
      event: VIRTUAL_PAGE_VIEW_EVENT,
      page_location: pageLocation,
      page_title: pageTitle,
    });
  }, [dataLayer, pageTitle, isLoading]);
}
